import {
  Banner,
  Button,
  GlobalConfirmationDialog,
  Input,
  InputGroup,
  useConfirmationDialogContext,
} from 'src/design-system'
import { Option, SelectInput } from 'components/atoms/select-input'
import { Plus, Trash } from '@phosphor-icons/react'
import * as React from 'react'
import { AiSkillContentFormVm } from './ai-skill-content-form-vm'
import { AiFormProps } from './forms.types'
import { modifyAtIndex, removeIndex } from 'app/packs/src/utils/array-helpers'
import { ConfirmationDialogProps } from 'app/packs/src/design-system/components/molecules/ConfirmationDialog'

type Keyword = {
  id: number
  value: string
}

type ExtendedAiFormProps = AiFormProps & {
  onCancel?: () => void
  isGenerating?: boolean
}

export const AiSkillContentForm = (props: ExtendedAiFormProps) => {
  const { inputs, startGeneration, onCancel, isGenerating = false } = props

  const { openConfirmationDialog } = useConfirmationDialogContext()

  const vm = new AiSkillContentFormVm(inputs.teamType as string)

  const [discipline, setDiscipline] = React.useState(
    vm.defaultDisciplineOption?.value,
  )
  const [numberLevels, setNumberLevels] = React.useState(
    vm.defaultLevelOption?.value,
  )
  const [keywords, setKeywords] = React.useState<Keyword[]>([
    { id: 1, value: '' },
  ])
  const [keywordError, setKeywordError] = React.useState<string | null>(null)

  const hasEmptyKeyword = React.useCallback(() => {
    return keywords.some((keyword) => keyword.value.trim().length === 0)
  }, [keywords])

  const performGeneration = () => {
    startGeneration({
      discipline,
      numberLevels,
      keywords: keywords.map((k) => k.value),
    })
  }

  const generate = () => {
    if (inputs.showWarning) {
      openConfirmationDialog('overwrite-skill-with-ai-content', {
        title: 'Overwrite existing content?',
        body: 'Generating all the levels will permanently overwrite the existing content.',
        confirmLabel: 'Generate all levels',
        onConfirm: performGeneration,
      } satisfies ConfirmationDialogProps)
    } else {
      performGeneration()
    }
  }

  return (
    <div className="text-gray-900">
      <GlobalConfirmationDialog id={`overwrite-skill-with-ai-content`} />
      {inputs.showWarning ? (
        <Banner variant="warning" className="mb-3 rounded justify-start">
          This will overwrite any existing content.
        </Banner>
      ) : null}
      <InputGroup.Root className="mb-5">
        <InputGroup.Label>Discipline</InputGroup.Label>
        <SelectInput
          name="discipline"
          options={vm.disciplineOptions}
          defaultValue={vm.defaultDisciplineOption}
          onChange={(option) =>
            setDiscipline(String((option as Option)?.value) || '')
          }
        />
      </InputGroup.Root>

      <InputGroup.Root className="mb-5">
        <InputGroup.Label>Keywords or phrases</InputGroup.Label>
        <ul className="pl-4">
          {keywords.map((keyword) => (
            <li key={keyword.id} className="mb-2">
              <>
                <Input
                  naked
                  placeholder="E.g. Attention to detail, optimistic"
                  className="py-2 px-1 w-[90%]"
                  value={keyword.value}
                  onChange={(e) => {
                    const index = keywords.findIndex((k) => k.id === keyword.id)
                    const newKeywords = modifyAtIndex(keywords, index, {
                      id: keywords[index].id,
                      value: e.target.value,
                    })
                    setKeywords(newKeywords)
                    if (e.target.value.trim().length > 0) {
                      setKeywordError(null)
                    }
                  }}
                />
                {keywords.length > 1 && (
                  <Trash
                    aria-label="Remove keyword"
                    className="text-gray-600 ml-2 mt-3.5 float-right cursor-pointer w-4"
                    onClick={() => {
                      const index = keywords.findIndex(
                        (k) => k.id === keyword.id,
                      )
                      setKeywords(removeIndex(keywords, index))
                      setKeywordError(null)
                    }}
                  />
                )}
              </>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          colourVariant="paper"
          onClick={() => {
            if (hasEmptyKeyword()) {
              setKeywordError('Please enter at least one character to proceed.')
              return
            }

            const maxId = Math.max(...keywords.map((k) => k.id))
            setKeywords([...keywords, { id: maxId + 1, value: '' }])
          }}
        >
          <Plus className="text-green-600" /> Add another
        </Button>
        {keywordError && (
          <p className="mt-2 mb-0 text-sm text-red-600" role="alert">
            {keywordError}
          </p>
        )}
      </InputGroup.Root>

      <InputGroup.Root className="mb-5">
        <InputGroup.Label>Number of levels *</InputGroup.Label>
        <SelectInput
          className="mb-2"
          name="numberLevels"
          options={vm.levelOptions}
          defaultValue={vm.defaultLevelOption}
          onChange={(option) =>
            setNumberLevels(Number((option as Option)?.value))
          }
        />
        <p className="text-gray-600 text-sm">
          We recommend 5 levels, regardless of how many positions are in a
          Framework
        </p>
      </InputGroup.Root>

      <div className="flex gap-3">
        <Button
          onClick={generate}
          disabled={isGenerating}
          className="flex-1 bg-green-600 text-white hover:bg-green-700"
        >
          {isGenerating ? 'Generating...' : 'Generate levels'}
        </Button>
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={false}
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  )
}
